// Typed fetch client for the Worker API. Cookies (session) are sent automatically. One
// household: no household ids in any URL, identity comes from the session.

import type { DayRecord, HouseholdSnapshot, Kid, Parent, PointEvent, PushPrefs, SessionInfo } from "../shared/types";
import type { History } from "../shared/history";

let onUnauthorized: () => void = () => undefined;

/** The store's one signed-out path, called on any 401 from any call. */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

/**
 * What to tell a person when a call failed: the server's own words when it answered, "No
 * connection" when it never did (offline, timed out, aborted), else `fallback`. Each browser
 * words a network failure differently: Chrome "Failed to fetch", Safari "Load failed",
 * Firefox "NetworkError…", and a timeout is "signal timed out".
 */
export function errorText(e: unknown, fallback: string): string {
  if (!(e instanceof Error) || !e.message) return fallback;
  const offline =
    e.name === "TimeoutError" ||
    e.name === "AbortError" ||
    /failed to fetch|load failed|networkerror|signal timed out|aborted/i.test(e.message);
  return offline ? "No connection. Try again." : e.message;
}

/** Long enough for a slow phone network, short enough that a hung call never blocks the UI. */
const TIMEOUT_MS = 15_000;

/**
 * Gives up after TIMEOUT_MS, or sooner when the caller's own signal aborts (a screen that moved
 * on). Joined by hand: AbortSignal.any is newer than some phones still in use.
 */
function deadline(signal?: AbortSignal | null): AbortSignal {
  const timeout = AbortSignal.timeout(TIMEOUT_MS);
  if (!signal) return timeout;
  const either = new AbortController();
  for (const s of [timeout, signal]) {
    if (s.aborted) either.abort(s.reason);
    else s.addEventListener("abort", () => either.abort(s.reason), { once: true });
  }
  return either.signal;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  // A call that never answers would leave the store waiting on it forever (taps in flight hold
  // back every refresh), so each one gives up after TIMEOUT_MS and rejects like any failure.
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    ...init,
    signal: deadline(init?.signal),
  });
  if (!res.ok) {
    if (res.status === 401) onUnauthorized();
    let message = res.statusText;
    try {
      message = ((await res.json()) as { error?: string }).error ?? message;
    } catch {
      /* not JSON: keep the status text */
    }
    const err = new Error(message) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as T;
}

const post = (body: unknown): RequestInit => ({ method: "POST", body: JSON.stringify(body) });

export const api = {
  // ---- auth ----
  requestCode: (email: string) => req<{ ok: true }>("/api/auth/request", post({ email })),

  verifyCode: (email: string, code: string) => req<{ ok: true }>("/api/auth/verify", post({ email, code })),

  kidSignIn: (code: string) => req<{ ok: true }>("/api/auth/kid", post({ code })),

  // Preview-only: sign in as the admin with one tap (the server 404s in production).
  previewLogin: () => req<{ ok: true }>("/api/auth/preview-login", { method: "POST" }),

  me: () => req<SessionInfo>("/api/auth/me"),

  logout: () => req<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  // ---- household ----
  snapshot: () => req<HouseholdSnapshot>("/api/snapshot"),

  // ---- kids (adults only) ----
  createKid: (name: string, colour: string) => req<Kid>("/api/kids", post({ name, colour })),

  editKid: (kidId: string, name: string, colour: string) =>
    req<Kid>(`/api/kids/${encodeURIComponent(kidId)}/edit`, post({ name, colour })),

  // Adults only. `name` is the kid's name as the grown-up typed it to confirm; the Worker checks it.
  removeKid: (kidId: string, name: string) =>
    req<{ ok: true }>(`/api/kids/${encodeURIComponent(kidId)}`, { method: "DELETE", body: JSON.stringify({ name }) }),

  mintKidCode: (kidId: string) =>
    req<{ code: string }>(`/api/kids/${encodeURIComponent(kidId)}/code`, { method: "POST" }),

  getKidCode: (kidId: string) => req<{ code: string | null }>(`/api/kids/${encodeURIComponent(kidId)}/code`),

  // Adults only. The body is the image itself (the 256px JPEG PhotoPicker makes), at most 300 KB.
  setKidPhoto: (kidId: string, photo: Blob) =>
    req<{ updatedAt: number }>(`/api/kids/${encodeURIComponent(kidId)}/photo`, {
      method: "PUT",
      headers: { "Content-Type": photo.type },
      body: photo,
    }),

  removeKidPhoto: (kidId: string) =>
    req<{ ok: true }>(`/api/kids/${encodeURIComponent(kidId)}/photo`, { method: "DELETE" }),

  // ---- points ----
  // Adults only: the Worker answers 403 to a child session.
  // `from` is this browser's push endpoint, if it has one, so the tap's push skips this phone.
  addEvent: (kidId: string, delta: number, reason: string | null, from: string | null) =>
    req<{ event: PointEvent; score: number }>(`/api/kids/${encodeURIComponent(kidId)}/events`, post({ delta, reason, from })),

  // Adults only. Any tap not held by a closed day (today's, in practice); 409 for one that is.
  deleteEvent: (eventId: string) =>
    req<{ score: number }>(`/api/events/${encodeURIComponent(eventId)}`, { method: "DELETE" }),

  // Any session. `day` is YYYY-MM-DD in the household timezone; events come oldest first,
  // with `opening`, the score the day started on.
  dayEvents: (kidId: string, day: string) =>
    req<{ events: PointEvent[]; opening: number }>(
      `/api/kids/${encodeURIComponent(kidId)}/events?day=${encodeURIComponent(day)}`,
    ),

  // ---- goals and closed days ----
  // Adults only. A whole number from 1 to 100; applies from today on.
  setGoal: (kidId: string, goal: number) =>
    req<{ goal: number }>(`/api/kids/${encodeURIComponent(kidId)}/goal`, post({ goal })),

  // Adults only. Today can be closed again until midnight; a closed past day comes back
  // unchanged. `fresh` is true only when this call won the star.
  closeDay: (kidId: string, day: string) =>
    req<DayRecord & { fresh: boolean }>(`/api/kids/${encodeURIComponent(kidId)}/close-day`, post({ day })),

  // Any session. Closed days from `from` to `to` inclusive (at most 400), oldest first.
  days: (kidId: string, from: string, to: string) =>
    req<{ days: DayRecord[] }>(
      `/api/kids/${encodeURIComponent(kidId)}/days?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),

  // Any session. One entry per day from `from` to `to` (at most 400 days), never past today and
  // never before the kid was added, with `opening`, the score just before the first one.
  // `signal` lets the Diary drop a read it no longer needs.
  history: (kidId: string, from: string, to: string, signal?: AbortSignal) =>
    req<History>(
      `/api/kids/${encodeURIComponent(kidId)}/history?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      { signal },
    ),

  // ---- parents (adults only; the list itself comes in the snapshot) ----
  inviteParent: (email: string) => req<Parent>("/api/parents", post({ email })),

  removeParent: (email: string) =>
    req<{ ok: true }>(`/api/parents/${encodeURIComponent(email)}`, { method: "DELETE" }),

  // ---- web push: taps as they happen, and the Sunday summary ----
  // Any session. The public VAPID key the browser subscribes with ("" if push is not set up).
  vapidKey: () => req<{ key: string }>("/api/push/vapid"),

  // Adults only. PushSubscription.toJSON() (endpoint plus keys) and which pushes this browser
  // wants. Sent again with new choices to change them.
  subscribePush: (sub: PushSubscriptionJSON, prefs: PushPrefs) =>
    req<{ ok: true }>("/api/push/subscribe", post({ ...sub, ...prefs })),

  // Adults only. Sends this browser a test push: "sent", "logged" (no push key on this server),
  // "gone" (its subscription expired and was forgotten) or "failed".
  testPush: (endpoint: string) =>
    req<{ outcome: "sent" | "logged" | "gone" | "failed" }>("/api/push/test", post({ endpoint })),

  // Adults only. What this browser gets, or null if the server has no subscription for it.
  pushStatus: (endpoint: string) => req<{ prefs: PushPrefs | null }>("/api/push/status", post({ endpoint })),

  // Adults only.
  unsubscribePush: (endpoint: string) =>
    req<{ ok: true }>("/api/push/subscribe", { method: "DELETE", body: JSON.stringify({ endpoint }) }),

  // ---- the intro film ----
  // Any session. Records `intro watched` in PostHog: watched to the end or not, and how far.
  introWatched: (completed: boolean, seconds: number) =>
    req<{ ok: true }>("/api/intro/watched", post({ completed, seconds })),
};
